import type { ReactNode } from 'react'
import {
  CircleAlert,
  CircleCheck,
  CircleDashed,
  TriangleAlert,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import type {
  PackagingItemHeavyMetalsAuditLatest,
  PackagingItemHeavyMetalsAuditStatus,
} from '@/shared/hooks/api/packagingItemsOverview'
import { formatRunTimestamp } from './PackagingFieldCrossCheckRunResults'
import {
  packagingHeavyMetalsAuditVerdictLabelKey,
  packagingItemHeavyMetalsAuditStatusLabelKey,
} from './packagingItemHeavyMetalsAuditFilter'

type PackagingItemHeavyMetalsAuditCellProps = {
  heavyMetalsAuditStatus: PackagingItemHeavyMetalsAuditStatus
  heavyMetalsAuditLatest: PackagingItemHeavyMetalsAuditLatest | null
  heavyMetalsAuditLastRunAt?: string | null
}

function HeavyMetalsAuditTableHoverCell({
  ariaLabel,
  icon,
  children,
}: {
  ariaLabel: string
  icon: ReactNode
  children: ReactNode
}) {
  return (
    <HoverCard openDelay={200}>
      <HoverCardTrigger asChild>
        <button
          type='button'
          className='inline-flex rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
          aria-label={ariaLabel}
          onClick={(event) => event.stopPropagation()}
        >
          {icon}
        </button>
      </HoverCardTrigger>
      <HoverCardContent align='start' className='w-80 space-y-2 text-sm'>
        {children}
      </HoverCardContent>
    </HoverCard>
  )
}

function heavyMetalsAuditStatusIcon(
  status: PackagingItemHeavyMetalsAuditStatus
): ReactNode {
  if (status === 'passed') {
    return (
      <CircleCheck
        className='size-4 text-green-600 dark:text-green-500'
        aria-hidden
      />
    )
  }
  if (status === 'failed') {
    return <CircleAlert className='size-4 text-destructive' aria-hidden />
  }
  if (status === 'flagged') {
    return (
      <TriangleAlert
        className='size-4 text-amber-600 dark:text-amber-500'
        aria-hidden
      />
    )
  }
  return <CircleDashed className='size-4 text-muted-foreground' aria-hidden />
}

export function PackagingItemHeavyMetalsAuditCell({
  heavyMetalsAuditStatus,
  heavyMetalsAuditLatest,
  heavyMetalsAuditLastRunAt,
}: PackagingItemHeavyMetalsAuditCellProps) {
  const { t, i18n } = useTranslation('packagingSuppliers')

  const statusLabel =
    heavyMetalsAuditStatus === 'not_audited'
      ? t('list.heavyMetalsAudit.notAudited')
      : t(packagingItemHeavyMetalsAuditStatusLabelKey(heavyMetalsAuditStatus))

  const runLabel =
    heavyMetalsAuditLastRunAt != null && heavyMetalsAuditLastRunAt !== ''
      ? formatRunTimestamp(heavyMetalsAuditLastRunAt, i18n.language)
      : null

  return (
    <HeavyMetalsAuditTableHoverCell
      ariaLabel={statusLabel}
      icon={heavyMetalsAuditStatusIcon(heavyMetalsAuditStatus)}
    >
      {heavyMetalsAuditStatus === 'not_audited' ? (
        <p>{statusLabel}</p>
      ) : null}
      {heavyMetalsAuditLatest != null ? (
        <>
          <p className='font-medium'>
            {t(
              packagingHeavyMetalsAuditVerdictLabelKey(
                heavyMetalsAuditLatest.verdict
              )
            )}
          </p>
          {runLabel != null ? (
            <p className='text-muted-foreground'>{runLabel}</p>
          ) : null}
          <p className='text-muted-foreground line-clamp-4'>
            {heavyMetalsAuditLatest.reasoningEn}
          </p>
        </>
      ) : heavyMetalsAuditStatus !== 'not_audited' ? (
        <p>{statusLabel}</p>
      ) : null}
    </HeavyMetalsAuditTableHoverCell>
  )
}
